import { css } from '@emotion/core'
import React from 'react'

interface VideoProps {
  id?: string
  srcType: {
    type: string
    src: string
  }[]
  ratio: number
  type?: string
  className?: string
  autoPlay?: boolean
  loop?: boolean
  thumbnail?: string
  playInView?: boolean
  loopDelay?: number
}

const Video: React.FunctionComponent<VideoProps> = ({
  id,
  srcType,
  ratio,
  className,
  autoPlay = true,
  loop = true,
  thumbnail,
  playInView = false,
  loopDelay,
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const [inView, setInView] = React.useState(false)

  React.useEffect(() => {
    const scrollHandler = () => {
      if (!inView && videoRef.current) {
        const rect = videoRef.current.getBoundingClientRect()
        if (rect.top < window.innerHeight && rect.bottom - 100 < window.innerHeight) {
          setInView(true)
        }
      }
    }

    if (playInView) {
      document.addEventListener('scroll', scrollHandler)
    }

    return () => {
      document.removeEventListener('scroll', scrollHandler)
    }
  }, [inView])

  React.useEffect(() => {
    const handler = () => {
      setTimeout(() => videoRef.current?.play(), loopDelay)
    }

    if (videoRef.current && loop && loopDelay) {
      videoRef.current.addEventListener('ended', handler)
    }

    return () => {
      videoRef.current?.removeEventListener('ended', handler)
    }
  }, [videoRef.current, loopDelay, loop])

  React.useEffect(() => {
    if (videoRef.current && autoPlay && (!playInView || (playInView && inView))) {
      videoRef.current.play()
    }
  }, [videoRef, autoPlay, playInView, inView])

  return (
    <div id={id} style={{ position: 'relative', overflow: 'hidden' }} className={className}>
      <div style={{ width: '100%', paddingBottom: `${((1 / ratio) * 100).toFixed(4)}%` }} />
      <video
        autoPlay={autoPlay && (!playInView || (playInView && inView))}
        playsInline
        loop={loop && !loopDelay}
        muted
        css={videoStyle}
        ref={videoRef}
        poster={thumbnail}
      >
        {srcType.map(({ src, type }, index) => (
          <source key={index} src={src} type={type} />
        ))}
        Your browser does not support the video tag.
      </video>
    </div>
  )
}

export default Video

const videoStyle = css`
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
`
